import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import 'bootstrap-icons/font/bootstrap-icons.css';
import GroceryStapleForm from '../components/GroceryStapleForm';
import RestaurantSpecialEditor from '../components/RestaurantSpecialEditor';
import RestaurantSpecialTrustBowl from '../components/RestaurantSpecialTrustBowl';
import useAuth from '../hooks/useAuth';
import {
  fetchGroceryStaples,
  getStoreKey,
  mergeStaplePrices,
} from '../utils/groceryStaples';
import {
  canEditRestaurantSpecials,
  fetchRestaurantSpecials,
  getRestaurantKey,
  mergeRestaurantSpecials,
} from '../utils/restaurantSpecials';
import {
  SYRACUSE_CENTER,
  GROCERY_STAPLES,
  fetchNearbyPlaces,
  isNearSyracuse,
  resolveBestLocation,
} from '../utils/nearbyPlaces';

// eslint-disable-next-line no-unused-vars
import { Tableau } from './test';
// eslint-disable-next-line no-unused-vars
import { Tableau2 } from './pc_chart';
const containerStyle = {
  width: '100%',
  height: '100%',
};

const MARKER_ICONS = {
  restaurant: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
  restaurantHighlight: 'https://maps.google.com/mapfiles/ms/icons/orange-dot.png',
  grocery: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png',
  groceryHighlight: 'https://maps.google.com/mapfiles/ms/icons/yellow-dot.png',
};

const getMarkerIcon = (highlighted, viewMode) => {
  const isRestaurant = viewMode === 'restaurants';
  const url = highlighted
    ? (isRestaurant ? MARKER_ICONS.restaurantHighlight : MARKER_ICONS.groceryHighlight)
    : (isRestaurant ? MARKER_ICONS.restaurant : MARKER_ICONS.grocery);
  const size = highlighted ? 42 : 32;
  return { url, scaledSize: { width: size, height: size } };
};
const itemKey = (item) => item.placeId || item.name;
// ==================== RESTAURANTS (17 original + 9 verified new) ====================
const restaurantLocations = [
  // === ORIGINAL 17 (unchanged names/specials, but with corrected locations, addresses, and phones) ===
  { 
    name: "Pastabilities", 
    location: { lat: 43.0483, lng: -76.1554 }, 
    special: "Soup of the Day + Pasta Special", 
    specialinfo: "Rotating homemade soup and fresh pasta of the day with Stretch Bread", 
    price: "$14–18",
    address: "311 S Franklin St, Syracuse, NY 13202",
    phone: "(315) 474-1153"
  },
  { 
    name: "Kitty Hoynes Irish Pub", 
    location: { lat: 43.0486, lng: -76.1552 }, 
    special: "Fish & Chips or Reuben", 
    specialinfo: "Classic Irish pub favorites with daily lunch specials", 
    price: "$14–18",
    address: "301 W Fayette St, Syracuse, NY 13202",
    phone: "(315) 424-1974"
  },
  { 
    name: "Coleman's Irish Pub (Tipp Hill)", 
    location: { lat: 43.0473, lng: -76.1684 }, 
    special: "Corned Beef Sandwich or Guinness Stew", 
    specialinfo: "Tipp Hill legend — often with fresh soda bread", 
    price: "$13–17",
    address: "100 S Lowell Ave, Syracuse, NY 13204",
    phone: "(315) 476-1933"
  },
  { 
    name: "Dinosaur Bar-B-Que", 
    location: { lat: 43.0526, lng: -76.1547 }, 
    special: "Pulled Pork Sandwich or Daily Special", 
    specialinfo: "Pit-smoked with famous sauce", 
    price: "$15–19",
    address: "246 W Willow St, Syracuse, NY 13202",
    phone: "(315) 476-4937"
  },
  { 
    name: "Stella's Diner", 
    location: { lat: 43.0728, lng: -76.1395 }, 
    special: "Daily Soup & Sandwich Special", 
    specialinfo: "Rotating comfort classics like hot turkey or kielbasa", 
    price: "$10–14",
    address: "110 Wolf St, Syracuse, NY 13208",
    phone: "(315) 425-0353"
  },
  { 
    name: "Lemon Grass Thai Restaurant", 
    location: { lat: 43.0482, lng: -76.1538 }, 
    special: "Pad Thai or Green Curry Special", 
    specialinfo: "Fresh daily Thai specials", 
    price: "$15–18",
    address: "113 Walton St, Syracuse, NY 13202",
    phone: "(315) 475-1111"
  },
  { 
    name: "Heid's of Liverpool", 
    location: { lat: 43.1065, lng: -76.2090 }, 
    special: "Coney Dog & Shake Combo", 
    specialinfo: "Iconic Syracuse hot dogs since 1917", 
    price: "$9–13",
    address: "305 Oswego St, Liverpool, NY 13088",
    phone: "(315) 451-0786"
  },
  { 
    name: "The Mission Mexican", 
    location: { lat: 43.0474, lng: -76.1486 }, 
    special: "Taco Tuesday or Burrito Special", 
    specialinfo: "Street-style tacos and rotating specials", 
    price: "$12–16",
    address: "304 E Onondaga St, Syracuse, NY 13202",
    phone: "(315) 475-7344"
  },
  { 
    name: "CopperTop Tavern", 
    location: { lat: 43.1553, lng: -76.1178 }, 
    special: "Lunch Special Sandwich", 
    specialinfo: "Daily lunch deals Mon–Fri until 3pm", 
    price: "$12–16",
    address: "7777 Brewerton Rd, Cicero, NY 13212",
    phone: "(315) 458-1999"
  },
  { 
    name: "The Kabob House (Liverpool)", 
    location: { lat: 43.1060, lng: -76.2005 }, 
    special: "Mixed Grill Kabob Plate", 
    specialinfo: "Grilled meats, rice, hummus & salad", 
    price: "$16–20",
    address: "409 Tulip St, Liverpool, NY 13088",
    phone: "(315) 314-7171"
  },
  { 
    name: "Gracie's Kitchen", 
    location: { lat: 43.0582, lng: -76.2100 }, 
    special: "Daily Comfort Food Special", 
    specialinfo: "Rotating home-style meals near Tipp Hill", 
    price: "$11–15",
    address: "527 Charles Ave, Solvay, NY 13209",
    phone: "(315) 299-7078"
  },
  { 
    name: "Bangkok Thai (Liverpool)", 
    location: { lat: 43.1354, lng: -76.2204 }, 
    special: "Daily Thai Curry Special", 
    specialinfo: "House-made sauces and fresh ingredients", 
    price: "$15–18",
    address: "7421 Oswego Rd, Liverpool, NY 13090",
    phone: "(315) 451-4621"
  },
  { 
    name: "Apizza Regionale", 
    location: { lat: 43.0518, lng: -76.1548 }, 
    special: "Spicy Soppressata & Hot Honey", 
    specialinfo: "Wood-fired with local hot honey", 
    price: "$16–20",
    address: "260 W Genesee St, Syracuse, NY 13202",
    phone: "(315) 802-2607"
  },
  { 
    name: "Pavone's Pizza Eatery (Tipp Hill)", 
    location: { lat: 43.0475, lng: -76.1505 }, 
    special: "Grandma Pie", 
    specialinfo: "Thick square crust with fresh mozzarella & basil", 
    price: "$15–19",
    address: "349 S Warren St, Syracuse, NY 13202",
    phone: "(315) 471-1980"
  },
  { 
    name: "Varsity Pizza", 
    location: { lat: 43.0415, lng: -76.1368 }, 
    special: "Varsity Special", 
    specialinfo: "Historic thin-crust since 1926", 
    price: "$14–17",
    address: "802 S Crouse Ave, Syracuse, NY 13210",
    phone: "(315) 478-1235"
  },
  { 
    name: "Twin Trees 57 (Liverpool)", 
    location: { lat: 43.1394, lng: -76.2213 }, 
    special: "White Spinach Pie", 
    specialinfo: "Garlic white sauce, ricotta & spinach", 
    price: "$15–18",
    address: "7608 Oswego Rd, Liverpool, NY 13090",
    phone: "(315) 652-2700"
  },
  { 
    name: "Francesca's Cucina", 
    location: { lat: 43.0580, lng: -76.1525 }, 
    special: "Daily Pasta or Chicken Francese", 
    specialinfo: "Homemade pasta with rotating Italian specials", 
    price: "$17–22",
    address: "545 N Salina St, Syracuse, NY 13208",
    phone: "(315) 425-1556"
  },
  // === 9 NEW VERIFIED RESTAURANTS ===
  { 
    name: "The Dark Horse Tavern", 
    location: { lat: 43.0348, lng: -76.0505 }, 
    special: "Burger of the Day or Fish Fry",
    specialinfo: "Cozy neighborhood tavern serving pub classics and daily specials since 2003",
    price: "$14–19",
    address: "4312 E Genesee St, Syracuse, NY 13214",
    phone: "(315) 446-3180"
  },
  { 
    name: "The Blarney Stone", 
    location: { lat: 43.0558, lng: -76.1725 }, 
    special: "Award-Winning Burgers & Wings",
    specialinfo: "Tipp Hill legend — cash-only spot famous for the best burgers in Syracuse",
    price: "$12–17",
    address: "314 Avery Ave, Syracuse, NY 13204",
    phone: "(315) 487-9675"
  },
  { 
    name: "Aurora Brewing Co.", 
    location: { lat: 43.0462, lng: -76.1898 }, 
    special: "Brewery Burger or Flatbread Pizza",
    specialinfo: "Craft beer taproom with fresh food and a lively atmosphere",
    price: "$13–18",
    address: "881 Van Rensselaer St, Syracuse, NY 13204",
    phone: "(315) 399-4040"
  },
  { 
    name: "Mario & Salvo's Pizza", 
    location: { lat: 43.0350, lng: -76.0490 }, 
    special: "NY Style Pizza or Calzone",
    specialinfo: "Authentic thin-crust pizza and Italian classics",
    price: "$15–20",
    phone: "(315) 446-8479",
    address: "4326 E Genesee St, DeWitt, NY 13214"
  },
  { 
    name: "Limp Lizard Bar & Grill", 
    location: { lat: 43.0158, lng: -76.1895 }, 
    special: "BBQ Ribs or Brisket Sandwich",
    specialinfo: "Award-winning barbecue, live music, and huge portions",
    price: "$15–22",
    address: "4628 Onondaga Blvd, Syracuse, NY 13219",
    phone: "(315) 472-7831"
  },
  { 
    name: "XO Taco", 
    location: { lat: 43.0488, lng: -76.1410 }, 
    special: "Creative Tacos & Margaritas",
    specialinfo: "Fun taco & tequila bar with bold flavors and great cocktails",
    price: "$13–18",
    address: "713 E Fayette St, Syracuse, NY 13210",
    phone: "(315) 303-1699"
  },
  { 
    name: "Alto Cinco", 
    location: { lat: 43.0412, lng: -76.1195 }, 
    special: "Enchiladas or Fish Tacos",
    specialinfo: "Fresh handmade Mexican food in the heart of the Westcott neighborhood",
    price: "$14–19",
    address: "526 Westcott St, Syracuse, NY 13210",
    phone: "(315) 422-6399"
  },
  { 
    name: "Redwood Diner", 
    location: { lat: 43.0568, lng: -76.0684 }, 
    special: "All-Day Breakfast Special",
    specialinfo: "Classic American diner serving breakfast favorites",
    price: "$9–15",
    address: "121 E Manlius St, East Syracuse, NY 13057",
    phone: "(315) 437-5790"
  },
  { 
    name: "Erawan Thai Restaurant", 
    location: { lat: 43.0544, lng: -76.0984 }, 
    special: "Pad Thai or Green Curry",
    specialinfo: "Long-time Syracuse favorite for authentic Thai cuisine",
    price: "$14–20",
    address: "2724 Erie Blvd E, Syracuse, NY 13224",
    phone: "(315) 445-8152"
  }
];
// ==================== GROCERY STORES (7 original + 8 new verified) ====================
const groceryStores = [
  // === ORIGINAL 7 (with corrected addresses, phones, and locations) ===
  { 
    name: "Wegmans (James St)", 
    location: { lat: 43.069, lng: -76.089 }, 
    eggs12: "$1.99", 
    milkGallon: "$3.59", 
    chickenLb: "$4.69", 
    beefLb: "$5.79", 
    phone: "(315) 437-1534",
    address: "4256 James St, East Syracuse, NY 13057"
  },
  { 
    name: "Aldi (Liverpool)", 
    location: { lat: 43.141, lng: -76.220 }, 
    eggs12: "$1.85", 
    milkGallon: "$3.19", 
    chickenLb: "$3.79", 
    beefLb: "$4.89", 
    phone: "(855) 955-2534",
    address: "7421 Oswego Rd, Liverpool, NY 13090"
  },
  { 
    name: "Price Chopper (Erie Blvd)", 
    location: { lat: 43.054, lng: -76.098 }, 
    eggs12: "$2.99", 
    milkGallon: "$3.69", 
    chickenLb: "$4.99", 
    beefLb: "$6.29", 
    phone: "(315) 449-2314",
    address: "2515 Erie Blvd E, Syracuse, NY 13224"
  },
  { 
    name: "Wegmans (Liverpool)", 
    location: { lat: 43.142, lng: -76.200 }, 
    eggs12: "$1.99", 
    milkGallon: "$3.49", 
    chickenLb: "$4.59", 
    beefLb: "$5.69", 
    phone: "(315) 457-1581",
    address: "7519 Oswego Rd, Liverpool, NY 13090"
  },
  { 
    name: "Price Chopper (Onondaga Blvd)", 
    location: { lat: 43.031, lng: -76.190 }, 
    eggs12: "$3.09", 
    milkGallon: "$3.79", 
    chickenLb: "$5.09", 
    beefLb: "$6.49", 
    phone: "(315) 472-3812",
    address: "4713 Onondaga Blvd, Syracuse, NY 13219"
  },
  { 
    name: "Tops Markets (North Syracuse)", 
    location: { lat: 43.134, lng: -76.138 }, 
    eggs12: "$2.79", 
    milkGallon: "$3.39", 
    chickenLb: "$4.79", 
    beefLb: "$5.99", 
    phone: "(315) 458-0032",
    address: "3803 Brewerton Rd, North Syracuse, NY 13212"
  },
  { 
    name: "Walmart Supercenter (Syracuse)", 
    location: { lat: 43.061, lng: -76.060 }, 
    eggs12: "$2.69", 
    milkGallon: "$3.29", 
    chickenLb: "$4.19", 
    beefLb: "$5.49", 
    phone: "(315) 434-9873",
    address: "6438 Basile Rowe, East Syracuse, NY 13057"
  },
  // === 8 NEW VERIFIED GROCERY STORES ===
  { 
    name: "Aldi (West Genesee)", 
    location: { lat: 43.054, lng: -76.168 }, 
    eggs12: "$1.85", 
    milkGallon: "$3.19", 
    chickenLb: "$3.79", 
    beefLb: "$4.89", 
    phone: "(855) 955-2534",
    address: "1055 West Genesee St, Syracuse, NY 13204"
  },
  { 
    name: "Aldi (Erie Blvd)", 
    location: { lat: 43.054, lng: -76.102 }, 
    eggs12: "$1.85", 
    milkGallon: "$3.19", 
    chickenLb: "$3.79", 
    beefLb: "$4.89", 
    phone: "(855) 955-2534",
    address: "2015 Erie Blvd E, Syracuse, NY 13224"
  },
  { 
    name: "Trader Joe's", 
    location: { lat: 43.0462, lng: -76.0706 }, 
    eggs12: "$2.79", 
    milkGallon: "$3.49", 
    chickenLb: "$4.49", 
    beefLb: "$5.99", 
    phone: "(315) 446-1629",
    address: "3422 Erie Blvd E, Syracuse, NY 13214"
  },
  { 
    name: "Wegmans (Fairmount)", 
    location: { lat: 43.0323, lng: -76.1905 }, 
    eggs12: "$1.99", 
    milkGallon: "$3.59", 
    chickenLb: "$4.79", 
    beefLb: "$6.29", 
    phone: "(315) 487-1581",
    address: "4722 Onondaga Blvd, Syracuse, NY 13219"
  },
  { 
    name: "Wegmans (Dewitt)", 
    location: { lat: 43.032, lng: -76.057 }, 
    eggs12: "$1.99", 
    milkGallon: "$3.65", 
    chickenLb: "$4.89", 
    beefLb: "$6.39", 
    phone: "(315) 446-1180",
    address: "6789 E Genesee St, Fayetteville, NY 13066"
  },
  { 
    name: "Tops (Nottingham Rd)", 
    location: { lat: 43.042, lng: -76.111 }, 
    eggs12: "$2.79", 
    milkGallon: "$3.55", 
    chickenLb: "$4.79", 
    beefLb: "$6.09", 
    phone: "(315) 446-1652",
    address: "620 Nottingham Rd, Syracuse, NY 13210"
  },
  { 
    name: "Tops (Shop City)", 
    location: { lat: 43.071, lng: -76.120 }, 
    eggs12: "$2.79", 
    milkGallon: "$3.59", 
    chickenLb: "$4.85", 
    beefLb: "$6.19", 
    phone: "(315) 437-5201",
    address: "181 Shop City Plaza, Syracuse, NY 13206"
  },
  { 
    name: "Tops (Brewerton Rd / North Syr)", 
    location: { lat: 43.134, lng: -76.138 }, 
    eggs12: "$2.79", 
    milkGallon: "$3.49", 
    chickenLb: "$4.69", 
    beefLb: "$5.89", 
    phone: "(315) 458-0032",
    address: "3803 Brewerton Rd, North Syracuse, NY 13212"
  }
];
const id = ["4b8f659271fb6001"];

const MOBILE_QUERY = '(max-width: 767px)';

const getDirectionsUrl = (item) => {
  const destination = item.address
    ? encodeURIComponent(item.address)
    : `${item.location.lat},${item.location.lng}`;
  return `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
};

const GroceryStapleList = ({ item, compact = false }) => (
  <ul className={compact ? 'map-location-staples map-location-staples--compact' : 'map-info-prices'}>
    {GROCERY_STAPLES.map(({ key, emoji, label }) => (
      <li key={key}>
        <span>{emoji} {label}</span>
        <span>{item[key] ?? '—'}</span>
      </li>
    ))}
  </ul>
);

const formatPriceUpdatedAt = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString();
};

const LocationInfoBody = ({
  item,
  mode,
  auth,
  onStapleSaved,
  onSpecialSaved,
  proposals = [],
  confirmThreshold = 3,
  onProposalSaved,
  onProposalConfirmed,
  showPriceForm = true,
}) => {
  if (mode === 'restaurants') {
    const isEditor = canEditRestaurantSpecials(auth?.roles);

    return (
      <div className="map-info-body">
        <p className="map-info-highlight">{item.special}</p>
        <p className="map-info-text">{item.specialinfo}</p>
        <p className="map-info-meta"><strong>Price:</strong> {item.price}</p>
        {item.hasCommunitySpecial && (
          <p className="map-info-note">
            {item.specialSource === 'community' ? 'Community-confirmed special' : 'Updated special'}
            {item.specialUpdatedBy ? ` · ${item.specialUpdatedBy}` : ''}
          </p>
        )}
        <p className="map-info-meta"><strong>Address:</strong> {item.address}</p>
        {item.phone && (
          <p className="map-info-meta"><strong>Phone:</strong> {item.phone}</p>
        )}
        {!item.isNearby && isEditor && (
          <RestaurantSpecialEditor
            item={item}
            auth={auth}
            onSaved={onSpecialSaved}
          />
        )}
        {!item.isNearby && (
          <RestaurantSpecialTrustBowl
            item={item}
            auth={auth}
            proposals={proposals}
            confirmThreshold={confirmThreshold}
            onProposalSaved={onProposalSaved}
            onProposalConfirmed={onProposalConfirmed}
          />
        )}
      </div>
    );
  }

  return (
    <div className="map-info-body">
      <GroceryStapleList item={item} />
      {item.hasCommunityPrices && (
        <p className="map-info-note">
          Community-reported prices
          {item.priceUpdatedBy ? ` · ${item.priceUpdatedBy}` : ''}
          {item.priceUpdatedAt ? ` · ${formatPriceUpdatedAt(item.priceUpdatedAt)}` : ''}
        </p>
      )}
      {!item.hasCommunityPrices && item.isNearby && (
        <p className="map-info-note">No community prices yet — add what you see in-store.</p>
      )}
      {item.isNearby && item.special && (
        <p className="map-info-meta"><strong>Rating:</strong> {item.special}</p>
      )}
      <p className="map-info-meta"><strong>Address:</strong> {item.address}</p>
      {showPriceForm && (
        <GroceryStapleForm
          item={item}
          auth={auth}
          onSaved={(data) => onStapleSaved?.(data)}
        />
      )}
      {item.phone && (
        <p className="map-info-meta"><strong>Phone:</strong> {item.phone}</p>
      )}
    </div>
  );
};

const InitMap = () => {
  const { auth } = useAuth();
  const [searchParams] = useSearchParams();
  const [selected, setSelected] = useState(null);
  const [communityPrices, setCommunityPrices] = useState({});
  const [communitySpecials, setCommunitySpecials] = useState({});
  const [specialProposals, setSpecialProposals] = useState({});
  const [confirmThreshold, setConfirmThreshold] = useState(3);
  const [hoveredKey, setHoveredKey] = useState(null);
  const [viewMode, setViewMode] = useState('restaurants');
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches
  );
  const [mobileMapItem, setMobileMapItem] = useState(null);
  const [mapsReady, setMapsReady] = useState(false);
  const [mapCenter, setMapCenter] = useState(SYRACUSE_CENTER);
  const [dataSource, setDataSource] = useState('loading');
  const [nearbyLocations, setNearbyLocations] = useState([]);
  const [nearbyLoading, setNearbyLoading] = useState(false);
  const [locationStatus, setLocationStatus] = useState(null);
  const mapRef = useRef(null);
  const mobileMapRef = useRef(null);
  const listItemRefs = useRef({});

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const view = searchParams.get('view');
    if (view === 'grocery' || view === 'restaurants') {
      setViewMode(view);
      setSelected(null);
      setHoveredKey(null);
    }
  }, [searchParams]);

  const applyResolvedLocation = useCallback((loc) => {
    setMapCenter(loc);
    if (mapRef.current) {
      mapRef.current.panTo(loc);
      mapRef.current.setZoom(13.2);
    }
    if (isNearSyracuse(loc)) {
      setDataSource('curated');
      setNearbyLocations([]);
      if (loc.source === 'network') {
        setLocationStatus(
          loc.label
            ? `Network location is near Syracuse (${loc.label}) — showing curated local picks.`
            : 'Network location is near Syracuse — showing curated local picks.'
        );
      } else {
        setLocationStatus(null);
      }
    } else {
      setDataSource('nearby');
      if (loc.viaVpn) {
        setLocationStatus(
          `VPN/network location detected${loc.label ? ` (${loc.label})` : ''}. Showing nearby places from Google Maps.`
        );
      } else if (loc.source === 'network') {
        setLocationStatus(
          `Using network location${loc.label ? ` (${loc.label})` : ''}. Showing nearby places from Google Maps.`
        );
      } else {
        setLocationStatus('Showing places near you from Google Maps.');
      }
    }
  }, []);

  const resolveUserLocation = useCallback((mode = 'auto') => {
    setDataSource('loading');
    setLocationStatus(null);
    setSelected(null);
    setHoveredKey(null);
    setMobileMapItem(null);

    return resolveBestLocation(mode)
      .then((loc) => {
        applyResolvedLocation(loc);
        return loc;
      })
      .catch(() => {
        setMapCenter(SYRACUSE_CENTER);
        setDataSource('curated');
        setNearbyLocations([]);
        setLocationStatus('Location unavailable — showing curated Syracuse picks.');
        if (mapRef.current) {
          mapRef.current.panTo(SYRACUSE_CENTER);
          mapRef.current.setZoom(13.2);
        }
      });
  }, [applyResolvedLocation]);

  useEffect(() => {
    if (!mapsReady) return;
    resolveUserLocation();
  }, [mapsReady, resolveUserLocation]);

  useEffect(() => {
    if (!mapsReady || dataSource !== 'nearby') return undefined;

    let cancelled = false;
    setNearbyLoading(true);

    fetchNearbyPlaces(mapCenter, viewMode)
      .then((places) => {
        if (!cancelled) {
          setNearbyLocations(places);
          setNearbyLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setNearbyLocations([]);
          setNearbyLoading(false);
          setLocationStatus('Could not load nearby places. Try again in a moment.');
        }
      });

    return () => { cancelled = true; };
  }, [mapsReady, dataSource, mapCenter, viewMode]);

  const curatedLocations = viewMode === 'restaurants' ? restaurantLocations : groceryStores;
  const currentLocations = dataSource === 'nearby' ? nearbyLocations : curatedLocations;
  const isLoading = dataSource === 'loading' || (dataSource === 'nearby' && nearbyLoading);

  const displayLocations = useMemo(() => {
    if (viewMode === 'grocery') {
      return mergeStaplePrices(currentLocations, communityPrices);
    }
    if (viewMode === 'restaurants' && dataSource === 'curated') {
      return mergeRestaurantSpecials(currentLocations, communitySpecials);
    }
    return currentLocations;
  }, [viewMode, dataSource, currentLocations, communityPrices, communitySpecials]);

  useEffect(() => {
    if (viewMode !== 'grocery' || !currentLocations.length) {
      setCommunityPrices({});
      return undefined;
    }

    let cancelled = false;
    const keys = currentLocations.map(getStoreKey);

    fetchGroceryStaples(keys)
      .then((data) => {
        if (!cancelled) setCommunityPrices(data);
      })
      .catch(() => {
        if (!cancelled) setCommunityPrices({});
      });

    return () => { cancelled = true; };
  }, [viewMode, currentLocations]);

  useEffect(() => {
    if (viewMode !== 'restaurants' || dataSource !== 'curated' || !currentLocations.length) {
      setCommunitySpecials({});
      setSpecialProposals({});
      return undefined;
    }

    let cancelled = false;
    const keys = currentLocations.map(getRestaurantKey);

    fetchRestaurantSpecials(keys, auth?.accessToken)
      .then((data) => {
        if (!cancelled) {
          setCommunitySpecials(data.published || {});
          setSpecialProposals(data.proposals || {});
          setConfirmThreshold(data.confirmThreshold || 3);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCommunitySpecials({});
          setSpecialProposals({});
        }
      });

    return () => { cancelled = true; };
  }, [viewMode, dataSource, currentLocations, auth?.accessToken]);

  const applySavedSpecial = useCallback((data) => {
    if (!data?.restaurantKey) return (item) => item;

    return (item) => {
      if (!item || getRestaurantKey(item) !== data.restaurantKey) return item;
      return {
        ...item,
        special: data.special || item.special,
        specialinfo: data.specialinfo || item.specialinfo,
        price: data.price || item.price,
        hasCommunitySpecial: true,
        specialUpdatedBy: data.updatedBy,
        specialUpdatedAt: data.updatedAt,
        specialSource: data.source,
      };
    };
  }, []);

  const handleSpecialSaved = useCallback((data) => {
    if (!data?.restaurantKey) return;

    setCommunitySpecials((prev) => ({
      ...prev,
      [data.restaurantKey]: data,
    }));
    setSpecialProposals((prev) => ({
      ...prev,
      [data.restaurantKey]: [],
    }));

    const updater = applySavedSpecial(data);
    setSelected((prev) => updater(prev));
    setMobileMapItem((prev) => updater(prev));
  }, [applySavedSpecial]);

  const handleProposalSaved = useCallback((proposal) => {
    if (!proposal?.id) return;

    const key = proposal.restaurantKey;
    setSpecialProposals((prev) => {
      const existing = prev[key] || [];
      const withoutOwn = existing.filter((entry) => entry.id !== proposal.id);
      return {
        ...prev,
        [key]: [proposal, ...withoutOwn],
      };
    });
  }, []);

  const handleProposalConfirmed = useCallback((data) => {
    if (data?.promoted) {
      handleSpecialSaved(data.promoted);
      return;
    }

    if (!data?.proposal) return;

    const key = data.proposal.restaurantKey;
    setSpecialProposals((prev) => ({
      ...prev,
      [key]: (prev[key] || []).map((entry) =>
        entry.id === data.proposal.id ? data.proposal : entry
      ),
    }));
  }, [handleSpecialSaved]);

  const handleStapleSaved = useCallback((data) => {
    if (!data?.storeKey) return;

    setCommunityPrices((prev) => ({
      ...prev,
      [data.storeKey]: data,
    }));

    const applySavedPrices = (item) => {
      if (!item || getStoreKey(item) !== data.storeKey) return item;
      const updated = {
        ...item,
        hasCommunityPrices: true,
        priceUpdatedBy: data.updatedBy,
        priceUpdatedAt: data.updatedAt,
      };
      GROCERY_STAPLES.forEach(({ key }) => {
        if (data[key]) updated[key] = data[key];
      });
      return updated;
    };

    setSelected((prev) => applySavedPrices(prev));
    setMobileMapItem((prev) => applySavedPrices(prev));
  }, []);

  const selectItem = useCallback((item) => {
    setSelected(item);
    setHoveredKey(null);
    if (mapRef.current) {
      mapRef.current.panTo(item.location);
      mapRef.current.setZoom(15);
    }
    listItemRefs.current[itemKey(item)]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    });
  }, []);

  const openMobileMap = (item) => {
    setSelected(item);
    setMobileMapItem(item);
  };

  const closeMobileMap = () => {
    setMobileMapItem(null);
  };

  useEffect(() => {
    if (mobileMapItem && mobileMapRef.current) {
      mobileMapRef.current.panTo(mobileMapItem.location);
      mobileMapRef.current.setZoom(15);
    }
  }, [mobileMapItem]);

  const switchView = (mode) => {
    setSelected(null);
    setHoveredKey(null);
    setMobileMapItem(null);
    setViewMode(mode);
    if (mapRef.current) {
      mapRef.current.panTo(mapCenter);
      mapRef.current.setZoom(13.2);
    }
  };

  const isSelected = (item) => selected && itemKey(selected) === itemKey(item);
  const isHovered = (item) => hoveredKey === itemKey(item) && !isSelected(item);
  const isHighlighted = (item) => isSelected(item) || isHovered(item);

  const renderListDetail = (item) => {
    if (viewMode === 'restaurants' || item.isNearby) {
      return item.special;
    }
    return `Eggs ${item.eggs12} · Milk ${item.milkGallon}`;
  };

  const showNearbyGroceryStaples = (item) =>
    viewMode === 'grocery' && item.isNearby;

  const pageTitle = dataSource === 'nearby' ? 'Food Map Near You' : 'Syracuse Food Map';

  const renderExpandedDetail = (item) => (
    <div className="map-location-item-expanded map-info-window">
      {showNearbyGroceryStaples(item) ? (
        <div className="map-info-body">
          {item.hasCommunityPrices && (
            <p className="map-info-note">
              Community-reported prices
              {item.priceUpdatedBy ? ` · ${item.priceUpdatedBy}` : ''}
            </p>
          )}
          {item.special && (
            <p className="map-info-meta"><strong>Rating:</strong> {item.special}</p>
          )}
          {item.specialinfo && (
            <p className="map-info-text">{item.specialinfo}</p>
          )}
          <GroceryStapleForm
            item={item}
            auth={auth}
            onSaved={handleStapleSaved}
            compact
          />
        </div>
      ) : (
        <LocationInfoBody
          item={item}
          mode={viewMode}
          auth={auth}
          onStapleSaved={handleStapleSaved}
          onSpecialSaved={handleSpecialSaved}
          proposals={specialProposals[getRestaurantKey(item)] || []}
          confirmThreshold={confirmThreshold}
          onProposalSaved={handleProposalSaved}
          onProposalConfirmed={handleProposalConfirmed}
        />
      )}
    </div>
  );

  const renderDirectionsButton = (item, className = 'map-directions-btn') => (
    <a
      href={getDirectionsUrl(item)}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
    >
      <i className="bi bi-sign-turn-right" /> Get directions
    </a>
  );

  return (
    <LoadScript
      mapIds={id}
      googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
      libraries={['places']}
      onLoad={() => setMapsReady(true)}
    >
    <div className={`map-page${isMobile ? ' map-page--mobile' : ''}`}>
      {isMobile && mobileMapItem && (
        <div className="map-mobile-overlay" role="dialog" aria-modal="true" aria-label={`Map for ${mobileMapItem.name}`}>
          <div className="map-mobile-overlay-map">
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={mobileMapItem.location}
              zoom={15}
              options={{ mapId: '4b8f659271fb6001' }}
              onLoad={(map) => { mobileMapRef.current = map; }}
            >
              <Marker
                position={mobileMapItem.location}
                icon={getMarkerIcon(true, viewMode)}
              />
            </GoogleMap>
          </div>
          <div className="map-mobile-overlay-panel">
            <button
              type="button"
              className="map-mobile-overlay-close"
              onClick={closeMobileMap}
              aria-label="Close map"
            >
              <i className="bi bi-x-lg" />
            </button>
            <h3 className="map-info-title">{mobileMapItem.name}</h3>
            <LocationInfoBody
              item={mobileMapItem}
              mode={viewMode}
              auth={auth}
              onStapleSaved={handleStapleSaved}
              onSpecialSaved={handleSpecialSaved}
              proposals={specialProposals[getRestaurantKey(mobileMapItem)] || []}
              confirmThreshold={confirmThreshold}
              onProposalSaved={handleProposalSaved}
              onProposalConfirmed={handleProposalConfirmed}
            />
            {renderDirectionsButton(mobileMapItem)}
          </div>
        </div>
      )}
      <div className="map-page-header">
        <div>
          <h1 className="map-page-title">{pageTitle}</h1>
          <p className="map-page-subtitle">
            {dataSource === 'nearby'
              ? (isMobile
                ? 'Nearby restaurants and grocery stores from Google Maps. Tap a location for details.'
                : 'Nearby restaurants and grocery stores from Google Maps. Select a pin or list item to explore.')
              : (isMobile
                ? 'Browse locations below. Tap for details, then View location for an in-app map.'
                : 'Compare lunch spots and grocery prices. Select a location or hover to highlight on the map.')}
          </p>
          {(locationStatus || dataSource === 'curated') && (
            <p className="map-location-status">
              {locationStatus || 'Showing curated Syracuse restaurants and grocery prices.'}
              <span className="map-location-actions">
                {locationStatus?.includes('unavailable') && (
                  <button
                    type="button"
                    className="map-location-retry"
                    onClick={() => resolveUserLocation('device')}
                  >
                    Use device GPS
                  </button>
                )}
                <button
                  type="button"
                  className="map-location-retry"
                  onClick={() => resolveUserLocation('network')}
                >
                  Use VPN / network location
                </button>
              </span>
            </p>
          )}
        </div>
        <div className="map-toggle-panel map-toggle-panel--inline">
          <button
            type="button"
            onClick={() => switchView('restaurants')}
            className={`map-toggle-btn ${viewMode === 'restaurants' ? 'active' : ''}`}
          >
            <i className="bi bi-shop" /> Restaurants
          </button>
          <button
            type="button"
            onClick={() => switchView('grocery')}
            className={`map-toggle-btn ${viewMode === 'grocery' ? 'active' : ''}`}
          >
            <i className="bi bi-cart3" /> {isMobile ? 'Grocery' : 'Grocery Prices'}
          </button>
        </div>
      </div>

      <div className="map-page-body">
        <div className="map-location-panel" role="region" aria-label="Map locations list">
          <div className="map-location-panel-header">
            <h2 className="map-location-panel-title">
              {viewMode === 'restaurants' ? 'Restaurants' : 'Grocery Stores'}
            </h2>
            <span className="map-location-count">
              {isLoading ? 'Loading…' : `${displayLocations.length} locations`}
            </span>
          </div>
          <ul className="map-location-list">
            {!isLoading && displayLocations.length === 0 && (
              <li className="map-location-empty">No locations found nearby. Try the other tab or refresh.</li>
            )}
            {displayLocations.map((item) => (
              <li
                key={itemKey(item)}
                ref={(el) => { listItemRefs.current[itemKey(item)] = el; }}
                className={`map-location-card${isSelected(item) ? ' selected' : ''}${isHovered(item) ? ' hovered' : ''}`}
              >
                <button
                  type="button"
                  className="map-location-item"
                  onClick={() => selectItem(item)}
                  onMouseEnter={() => !isMobile && setHoveredKey(itemKey(item))}
                  onMouseLeave={() => !isMobile && setHoveredKey(null)}
                  onFocus={() => !isMobile && setHoveredKey(itemKey(item))}
                  onBlur={() => !isMobile && setHoveredKey(null)}
                >
                  <span className="map-location-item-name">{item.name}</span>
                  {showNearbyGroceryStaples(item) ? (
                    <GroceryStapleList item={item} compact />
                  ) : (
                    <span className="map-location-item-detail">{renderListDetail(item)}</span>
                  )}
                  {item.address && (
                    <span className="map-location-item-address">{item.address}</span>
                  )}
                  {isMobile && isSelected(item) && renderExpandedDetail(item)}
                </button>
                {isMobile && (
                  <button
                    type="button"
                    className="map-view-location-btn"
                    onClick={() => openMobileMap(item)}
                  >
                    <i className="bi bi-geo-alt" /> View location
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>

        {!isMobile && (
        <div className="map-container">
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={mapCenter}
          zoom={13.2}
          options={{ mapId: "4b8f659271fb6001" }}
          onLoad={(map) => { mapRef.current = map; }}
        >
          {displayLocations.map(item => (
            <Marker
              key={itemKey(item)}
              position={item.location}
              icon={getMarkerIcon(isHighlighted(item), viewMode)}
              zIndex={isSelected(item) ? 1000 : isHovered(item) ? 500 : 1}
              onClick={() => selectItem(item)}
              onMouseOver={() => setHoveredKey(itemKey(item))}
              onMouseOut={() => setHoveredKey(null)}
            />
          ))}

          {selected?.location && (
            <InfoWindow
              position={selected.location}
              clickable={true}
              onCloseClick={() => setSelected(null)}
              options={{ maxWidth: 240 }}
            >
              <div className="map-info-window">
                <h3 className="map-info-title">{selected.name}</h3>
                <LocationInfoBody
                  item={selected}
                  mode={viewMode}
                  auth={auth}
                  onStapleSaved={handleStapleSaved}
                  onSpecialSaved={handleSpecialSaved}
                  proposals={specialProposals[getRestaurantKey(selected)] || []}
                  confirmThreshold={confirmThreshold}
                  onProposalSaved={handleProposalSaved}
                  onProposalConfirmed={handleProposalConfirmed}
                />
                {renderDirectionsButton(selected, 'map-directions-btn map-directions-btn--compact')}
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
        </div>
        )}
      </div>
    </div>
    </LoadScript>
  );
};

export default React.memo(InitMap);
